import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Alert,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { Add as AddIcon, Tag as BusinessIcon, Edit as EditIcon, Delete as DeleteIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import axios from 'axios';
import { usePageTitle } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import DataTable, { ColumnDefinition, SortConfig } from '../components/DataTable';
import ResourceLimitCard from '../components/common/ResourceLimitCard';
import CollapsibleInfoCard from '../components/common/CollapsibleInfoCard';
import CreateAccountModal from '../components/settings/CreateAccountModal';
import EditAccountModal from '../components/settings/EditAccountModal';
import { useCreateWithTrialCheck } from '../hooks/useCreateWithTrialCheck';
import TrialStatusModal from '../components/TrialStatusModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface AccountItem {
  id: string;
  name: string;
  description?: string;
  sector: string | { id: string; name: string; slug: string; [key: string]: any };
  createdAt: string;
}

interface FilterConfig {
  name?: string;
  sector?: string;
}

// Helper function to get sector name from account  
const getSectorName = (account: AccountItem): string => {
  if (typeof account.sector === 'object' && account.sector?.name) {
    return account.sector.name;
  }
  if (typeof account.sector === 'string') {
    return account.sector;
  }
  return 'Unknown';
};

// Mobile card component
const AccountCard: React.FC<{ account: AccountItem; isAdmin: boolean; onEdit: () => void; onDelete: () => void }> = ({ account, isAdmin, onEdit, onDelete }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
            {account.name}
          </Typography>
          <Box>
            <IconButton 
              size="small" 
              color="primary" 
              onClick={onEdit} 
              disabled={!isAdmin}
              sx={{ mr: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              color="error" 
              onClick={onDelete} 
              disabled={!isAdmin}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sector: {getSectorName(account)}
        </Typography>
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(account.createdAt).toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

const AccountsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  usePageTitle('Social Accounts');
  
  // Trial check for create button
  const {
    handleCreateAction,
    isTrialStatusModalOpen,
    closeTrialStatusModal,
    isAdmin: trialAdmin
  } = useCreateWithTrialCheck();

  const [orgInfo, setOrgInfo] = useState<any>(null);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({});
  const [showFilters, setShowFilters] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editAccountId, setEditAccountId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    fetchOrgInfo();
  }, []);

  useEffect(() => {
    fetchAccounts(page + 1, rowsPerPage);
  }, [page, rowsPerPage, sortConfig, filterConfig]);

  const fetchOrgInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/organization`, {
        withCredentials: true
      });
      setOrgInfo(res.data.organization);
    } catch (e) {
      // non-blocking
    }
  };

  const fetchAccounts = async (pageNum = 1, limit = 10) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNum,
        limit,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...filterConfig
      };
      const res = await axios.get(`${API_URL}/api/accounts`, {
        withCredentials: true,
        params
      });
      const items: AccountItem[] = (res.data.accounts || []).map((a: any) => ({
        id: a.id,
        name: a.name,
        description: a.description,
        sector: a.sector,
        createdAt: a.createdAt
      }));
      setAccounts(items);
      setTotalCount(res.data.pagination?.total ?? items.length);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'desc' });
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const handleFilterChange = (key: keyof FilterConfig, value: any) => setFilterConfig(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilterConfig({});

  const columns: ColumnDefinition<AccountItem>[] = [
    { key: 'name', label: 'Name', render: (row) => <Typography sx={{ fontWeight: 'medium' }}>{row.name}</Typography> },
    { key: 'sector', label: 'Sector', render: (row) => <Typography>{getSectorName(row)}</Typography> },
    { key: 'createdAt', label: 'Created Date', render: (row) => <Typography>{new Date(row.createdAt).toLocaleString()}</Typography> },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (row) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => isAdmin && setEditAccountId(row.id)} disabled={!isAdmin}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => isAdmin && setDeleteId(row.id)} disabled={!isAdmin}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  const mobileSortOptions = [
    { key: 'name', label: 'Name' },
    { key: 'sector', label: 'Sector' },
    { key: 'createdAt', label: 'Created Date' }
  ];

  const renderMobileCard = (account: AccountItem) => (
    <AccountCard 
      key={account.id}
      account={account} 
      isAdmin={isAdmin}
      onEdit={() => setEditAccountId(account.id)}
      onDelete={() => setDeleteId(account.id)}
    />
  );

  if (loading && accounts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ mb: { xs: 0, sm: 1 } }}>
          Social Accounts
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleCreateAction(() => setIsCreateOpen(true))} disabled={!isAdmin} fullWidth={isMobile}>
          New Account
        </Button>
      </Box>

      {/* Info */}
      <CollapsibleInfoCard
        title="About Social Accounts"
        content="Accounts defined here will be available as selections when creating simulations & ideas. When running a simulation or idea, you'll be able to choose which account the post is coming from."
      />

      {/* Limits */}
      {orgInfo && (
        <ResourceLimitCard
          icon={<BusinessIcon sx={{ color: 'primary.main' }} />}
          title="Accounts"
          used={totalCount}
          limit={orgInfo.plan === 'INTERNAL' ? null : (orgInfo.accountLimit ?? null)}
          isAdmin={isAdmin}
          sx={{ mb: 2 }}
        />
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: { xs: 2, sm: 0 } }}>
        <Typography variant="h6" component="h2">All Accounts</Typography>
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setShowFilters(!showFilters)} fullWidth={isMobile}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Paper component="aside" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" component="h3" gutterBottom>Filter by:</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Name" value={filterConfig.name || ''} onChange={(e) => handleFilterChange('name', e.target.value)} placeholder="Search by name" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth size="small" label="Sector" value={filterConfig.sector || ''} onChange={(e) => handleFilterChange('sector', e.target.value)} placeholder="Search by sector" />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={clearFilters} variant="outlined" color="primary" fullWidth={isMobile}>Clear Filters</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Table */}
      <Box>
        <DataTable
          data={accounts}
          columns={columns}
          sortConfig={sortConfig}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          renderMobileCard={renderMobileCard}
          mobileSortOptions={mobileSortOptions}
          tableAriaLabel="accounts table"
        />
      </Box>

      {/* Modals */}
      <CreateAccountModal open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onAccountCreated={() => { setIsCreateOpen(false); fetchAccounts(page + 1, rowsPerPage); fetchOrgInfo(); }} />
      <EditAccountModal open={!!editAccountId} onClose={() => setEditAccountId(null)} account={accounts.find(a => a.id === editAccountId) as any} onAccountUpdated={() => { setEditAccountId(null); fetchAccounts(page + 1, rowsPerPage); }} onDeleteAccount={(acct: any) => setDeleteId(acct.id)} />

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to delete this account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try {
              await axios.delete(`${API_URL}/api/accounts/${deleteId}`, { withCredentials: true });
              setDeleteId(null);
              fetchAccounts(page + 1, rowsPerPage);
              fetchOrgInfo();
            } catch (e) {
              setDeleteId(null);
            }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
    
    {/* Trial expired modal for create button clicks */}
    <TrialStatusModal 
      isOpen={isTrialStatusModalOpen} 
      onClose={closeTrialStatusModal} 
      isAdmin={trialAdmin}
      status="expired"
    />
    </>
  );
};

export default AccountsPage;


